import { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./components/ui/dialog";
import { Button } from "./components/ui/button";
import { Badge } from "./components/ui/badge";
import { Input } from "./components/ui/input";
import { Textarea } from "./components/ui/textarea";
import { ExportUndo, exportedToast } from "./ExportUndo";
import { SubjectStats } from "./SubjectStats";
import { HostnetPanel } from "./HostnetPanel";
import { PermissionFollowUp } from "./PermissionFollowUp";
import { UserPlus, RefreshCw, Mail, CheckCircle2, Ban, MessageSquare, BarChart3, Copy, Download, Clock, SkipForward, ExternalLink, Rocket, Users, Upload, X, Wrench } from "lucide-react";

const STATUS_STYLES = {
  queued: "bg-slate-100 text-slate-600",
  active: "bg-blue-100 text-blue-700",
  completed: "bg-emerald-100 text-emerald-700",
  replied: "bg-amber-100 text-amber-700",
  customer: "bg-green-600 text-white",
  unsubscribed: "bg-red-100 text-red-700",
  paused: "bg-slate-200 text-slate-500",
  bounced: "bg-red-100 text-red-700",
};
const STATUS_LABELS = {
  queued: "In wachtrij", active: "Loopt", completed: "Afgerond", replied: "Gereageerd",
  customer: "Klant", unsubscribed: "Afgemeld", paused: "Gepauzeerd", bounced: "Bounce",
};

const GENERIC_LOCALS = ["info", "contact", "mail", "email", "post", "welkom", "algemeen",
  "administratie", "secretariaat", "secretariat", "office", "sales", "receptie", "balie"];
const addrKind = (email) => {
  if (!email || !email.includes("@")) return null;
  const lp = email.split("@")[0].toLowerCase().replace(/[._-]/g, "");
  return GENERIC_LOCALS.some((g) => lp.startsWith(g)) ? "algemeen" : "persoonlijk";
};

const isDue = (r) =>
  r.status === "active" && r.step >= 1 && r.step < 3 && r.next_send_at && new Date(r.next_send_at) <= new Date();

const fmtDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return `${d.getDate()}-${d.getMonth() + 1}`;
};

const SubjectVariants = ({ step, subjects, onChange }) => (
  <div className="mb-2 flex flex-col gap-1.5">
    {subjects.map((v, j) => (
      <div key={j} className="flex gap-1.5 items-center">
        <Input data-testid={`crm-tpl-subject-${step}-${j}`} value={v} placeholder={`Onderwerp ${j + 1}`}
          onChange={(e) => onChange(subjects.map((x, k) => (k === j ? e.target.value : x)))} />
        {subjects.length > 1 && (
          <button data-testid={`crm-tpl-subject-del-${step}-${j}`} onClick={() => onChange(subjects.filter((_, k) => k !== j))}
            className="text-slate-400 hover:text-red-600" title="Verwijder variant"><X className="w-4 h-4" /></button>
        )}
      </div>
    ))}
    <div className="flex items-center justify-between text-[11px] text-slate-400">
      <span>{subjects.length > 1 ? "Elke lead krijgt willekeurig één van deze onderwerpen." : "Voeg varianten toe om onderwerpen te testen."}</span>
      {subjects.length < 5 && (
        <button data-testid={`crm-tpl-subject-add-${step}`} onClick={() => onChange([...subjects, ""])}
          className="font-medium text-[#FF4500] hover:underline">+ Onderwerp-variant</button>
      )}
    </div>
  </div>
);

export default function Crm({ open, onOpenChange, API, profile, profileLabel, autoHelper, onAutoHelperConsumed, batchLeads, batchNonce }) {
  const [pipeline, setPipeline] = useState({ counts: {}, total: 0, rows: [] });
  const [settings, setSettings] = useState(null);
  const [steps, setSteps] = useState([]);
  const [permission, setPermission] = useState(null);
  const [stats, setStats] = useState(null);
  const [exportStep, setExportStep] = useState(1);
  const [helperOpen, setHelperOpen] = useState(false);
  const [queue, setQueue] = useState([]);
  const [qIndex, setQIndex] = useState(0);
  const [current, setCurrent] = useState(null);
  const [loadingMail, setLoadingMail] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(600);
  const [timerOn, setTimerOn] = useState(false);
  const [helperStep, setHelperStep] = useState(null);
  const [contactOpen, setContactOpen] = useState(false);
  const [contactLead, setContactLead] = useState(null);
  const [contactForm, setContactForm] = useState({ name: "", title: "", email: "", phone: "" });
  const [importing, setImporting] = useState(false);
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [exportNonce, setExportNonce] = useState(0);
  const [sendersText, setSendersText] = useState("");

  const load = useCallback(async () => {
    const [p, s, t, st] = await Promise.all([
      axios.get(`${API}/crm/pipeline`, { params: { profile } }),
      axios.get(`${API}/crm/settings`),
      axios.get(`${API}/crm/templates`, { params: { profile } }),
      axios.get(`${API}/crm/stats`, { params: { profile } }),
    ]);
    setPipeline(p.data);
    setSettings(s.data);
    setSendersText(((s.data.from_emails && s.data.from_emails.length) ? s.data.from_emails : [s.data.from_email].filter(Boolean)).join("\n"));
    setSteps(t.data.steps.map((x) => ({ ...x, subjects: (x.subjects && x.subjects.length) ? x.subjects : [x.subject] })));
    const pm = t.data.permission || {};
    setPermission({ ...pm, subjects: (pm.subjects && pm.subjects.length) ? pm.subjects : [pm.subject || ""] });
    setExportNonce((n) => n + 1);
    setStats(st.data);
  }, [API, profile]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const enroll = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/crm/enroll`, { profile });
      toast.success(`${data.enrolled} ingeschreven, ${data.skipped} overgeslagen`);
      load();
    } catch { toast.error("Inschrijven mislukt"); } finally { setBusy(false); }
  };

  const mark = async (id, status) => {
    try { await axios.post(`${API}/crm/mark`, { club_id: id, status }); load(); }
    catch { toast.error("Bijwerken mislukt"); }
  };

  const saveTemplates = async () => {
    try {
      const clean = steps.map((x) => {
        const subjects = (x.subjects || []).map((v) => v.trim()).filter(Boolean);
        return { ...x, subjects, subject: subjects[0] || x.subject };
      });
      const ps = (permission?.subjects || []).map((v) => v.trim()).filter(Boolean);
      await axios.put(`${API}/crm/templates`, { profile, steps: clean,
        permission: permission ? { ...permission, subjects: ps, subject: ps[0] || permission.subject } : null });
      toast.success("Sjablonen opgeslagen");
    } catch { toast.error("Opslaan mislukt"); }
  };

  const setStep = (i, field, value) =>
    setSteps((prev) => prev.map((s, idx) => (idx === i ? { ...s, [field]: value } : s)));

  const saveLogo = async () => {
    try {
      const { data } = await axios.put(`${API}/crm/settings`, { logo_url: settings?.logo_url || "" });
      setSettings(data);
      toast.success("Logo opgeslagen — zit in de HTML-versie van de CSV");
    } catch { toast.error("Opslaan mislukt"); }
  };

  const saveFrom = async () => {
    const list = sendersText.split(/[\n,;]+/).map((v) => v.trim()).filter(Boolean);
    const bad = list.find((v) => !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v));
    if (bad) { toast.error(`Geen geldig e-mailadres: ${bad}`); return; }
    if (list.length > 5) { toast.error("Maximaal 5 afzenders"); return; }
    try {
      const { data } = await axios.put(`${API}/crm/settings`, { from_emails: list, from_email: list[0] || "" });
      setSettings(data);
      toast.success(list.length ? `${list.length} afzender(s) opgeslagen` : "Afzenders gewist");
    } catch { toast.error("Opslaan mislukt"); }
  };

  const exportMails = async () => {
    try {
      const res = await axios.get(`${API}/crm/export-mails`, { params: { profile, step: exportStep }, responseType: "blob" });
      const n = Number(res.headers["x-exported"] ?? 0);
      const skipped = Number(res.headers["x-skipped"] ?? 0);
      if (!n) { toast.info(`Niemand is nu aan de beurt voor Mail ${exportStep}${skipped ? ` (${skipped} overgeslagen)` : ""}`); return; }
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `mails_${profile}_mail${exportStep}_${n}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      exportedToast(API, res, exportStep, load);
      load();
    } catch { toast.error("Export mislukt"); }
  };

  const resetPipeline = async () => {
    if (!window.confirm(`Alle ingeschreven ${profileLabel.toLowerCase()} terugzetten naar Mail 1?\n\nAfgemelde, gereageerde en klant-leads blijven ongemoeid. Dit wist ook de verzend-historie van deze leads.`)) return;
    try {
      const { data } = await axios.post(`${API}/crm/reset-pipeline`, null, { params: { profile } });
      toast.success(`${data.reset} leads teruggezet naar Mail 1`);
      load();
    } catch { toast.error("Resetten mislukt"); }
  };

  const repairFailed = async () => {
    if (!window.confirm("Leads die onterecht een stap verder staan (verzending mislukt, niet door jou gemarkeerd) terugzetten naar hun echte stap?\n\nDoor jou gemarkeerde verzendingen (met tijdstempel) blijven staan.")) return;
    try {
      const { data } = await axios.post(`${API}/crm/repair-failed`);
      toast.success(`${data.repaired} leads hersteld`);
      load();
    } catch { toast.error("Herstellen mislukt"); }
  };

  const copyHtmlMail = async () => {
    if (!current) return;
    try {
      const html = current.html || `<div>${(current.body || "").replace(/\n/g, "<br>")}</div>`;
      const text = current.body || "";
      if (navigator.clipboard && window.ClipboardItem) {
        await navigator.clipboard.write([new window.ClipboardItem({
          "text/html": new Blob([html], { type: "text/html" }),
          "text/plain": new Blob([text], { type: "text/plain" }),
        })]);
      } else {
        await navigator.clipboard.writeText(text);
      }
      toast.success("Mail mét logo gekopieerd — plak in Outlook/Gmail");
    } catch {
      toast.error("Kopiëren mislukt");
    }
  };

  const copyMail = async (id) => {
    try {
      const { data } = await axios.get(`${API}/crm/render-mail`, { params: { club_id: id } });
      const text = `Aan: ${data.to}\nVan: ${data.from}\nOnderwerp: ${data.subject}\n\n${data.body}`;
      await navigator.clipboard.writeText(text);
      toast.success(`Mail (stap ${data.step}) gekopieerd — plak hem in je mailprogramma`);
    } catch {
      toast.error("Kopiëren mislukt");
    }
  };

  const loadMail = useCallback(async (row) => {
    if (!row) { setCurrent(null); return; }
    setLoadingMail(true);
    try {
      const { data } = await axios.get(`${API}/crm/render-mail`, { params: { club_id: row.id } });
      setCurrent(data);
    } catch { setCurrent(null); } finally { setLoadingMail(false); }
  }, [API]);

  const buildQueue = (onlyDue, stepFilter) => {
    const all = (pipeline.rows || []);
    let base = onlyDue
      ? all.filter(isDue)
      : all.filter((r) => r.status === "queued" || r.status === "active");
    if (stepFilter) base = base.filter((r) => ((r.step || 0) + 1) === stepFilter);
    const kindRank = (r) => (addrKind(r.email) === "persoonlijk" ? 0 : 1);
    return [...base].sort((a, b) =>
      kindRank(a) - kindRank(b)
      || (onlyDue
        ? (a.next_send_at || "").localeCompare(b.next_send_at || "")
        : (a.name || "").localeCompare(b.name || "")));
  };

  const openHelper = (onlyDue = false) => {
    const q = buildQueue(onlyDue, null);
    setHelperStep(null);
    setQueue(q);
    setQIndex(0);
    setSecondsLeft(600);
    setTimerOn(false);
    setHelperOpen(true);
    loadMail(q[0]);
  };

  const pickStep = (step) => {
    const q = buildQueue(false, step);
    setHelperStep(step);
    setQueue(q);
    setQIndex(0);
    setSecondsLeft(600);
    loadMail(q[0]);
  };

  const batchRef = useRef(0);
  useEffect(() => {
    if (open && batchNonce && batchNonce !== batchRef.current && (batchLeads || []).length) {
      batchRef.current = batchNonce;
      const kindRank = (r) => (addrKind(r.email) === "persoonlijk" ? 0 : 1);
      const q = [...batchLeads].sort((a, b) =>
        kindRank(a) - kindRank(b) || (a.name || "").localeCompare(b.name || ""));
      setHelperStep("batch");
      setQueue(q);
      setQIndex(0);
      setSecondsLeft(600);
      setTimerOn(false);
      setHelperOpen(true);
      loadMail(q[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, batchNonce, batchLeads]);

  const closeHelper = () => {
    setHelperOpen(false);
    setTimerOn(false);
    load();
  };

  useEffect(() => {
    if (open && autoHelper && (pipeline.rows || []).length > 0 && !helperOpen) {
      openHelper(false);
      onAutoHelperConsumed && onAutoHelperConsumed();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, autoHelper, pipeline]);

  const advance = (q = queue, idx = qIndex) => {
    const next = idx + 1;
    setQIndex(next);
    loadMail(q[next]);
    setSecondsLeft(600);
  };

  const openInMail = () => {
    if (!current) return;
    const url = `mailto:${encodeURIComponent(current.to)}?subject=${encodeURIComponent(current.subject)}&body=${encodeURIComponent(current.body)}`;
    const a = document.createElement("a");
    a.href = url;
    a.click();
  };

  const markSent = async () => {
    if (!current) return;
    try {
      await axios.post(`${API}/crm/mark-sent`, { club_id: current.club_id });
      toast.success("Gemarkeerd als verzonden");
      advance();
    } catch { toast.error("Bijwerken mislukt"); }
  };

  const fmt = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

  const openContact = (row) => {
    setContactLead(row);
    const ct = row.contact || {};
    setContactForm({ name: ct.name || "", title: ct.title || "", email: ct.email || "", phone: ct.phone || "" });
    setContactOpen(true);
  };

  const saveContact = async () => {
    try {
      await axios.patch(`${API}/crm/contact`, { club_id: contactLead.id, ...contactForm });
      toast.success("Contactpersoon opgeslagen");
      setContactOpen(false);
      load();
    } catch { toast.error("Opslaan mislukt"); }
  };

  const clearContact = async () => {
    try {
      await axios.delete(`${API}/crm/contact`, { params: { club_id: contactLead.id } });
      setContactOpen(false);
      load();
    } catch { toast.error("Verwijderen mislukt"); }
  };

  const importReplies = async (e) => {
    const file = e.target.files?.[0];
    if (file) e.target.value = "";
    if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/crm/import-replies?profile=${profile}`, fd);
      toast.success(`${data.updated} leads op 'Gereageerd' gezet${data.unmatched ? `, ${data.unmatched} niet gevonden` : ""}`);
      load();
    } catch { toast.error("Import mislukt"); } finally { setImporting(false); }
  };

  useEffect(() => {
    if (!timerOn) return;
    const t = setInterval(() => {
      setSecondsLeft((s) => {
        if (s <= 1) { toast.info("Tijd voor de volgende mail ⏰"); return 600; }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(t);
  }, [timerOn]);

  const c = pipeline.counts || {};
  const dueRows = (pipeline.rows || []).filter(isDue);

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2">
            <Mail className="w-5 h-5 text-[#FF4500]" /> CRM &amp; outreach — {profileLabel}
          </DialogTitle>
          <DialogDescription>
            Schrijf leads in voor een reeks van 3 mails in 2 weken. Verzenden gaat via je
            Outlook-add-on (CSV) of de Verzendhulp.
          </DialogDescription>
        </DialogHeader>

        {settings && (
          <div className="flex flex-wrap items-center gap-3 rounded-lg border border-slate-200 p-3 bg-slate-50">
            <div className="flex flex-wrap gap-2">
              <Button data-testid="crm-enroll-btn" size="sm" onClick={enroll} disabled={busy} className="rounded-full bg-[#FF4500] hover:bg-[#E63E00] text-white">
                <UserPlus className="w-4 h-4 mr-1.5" /> Schrijf {profileLabel.toLowerCase()} in
              </Button>
              <div className="flex items-center gap-1.5 rounded-full border border-slate-300 pl-3 pr-1 py-0.5">
                <span className="text-xs text-slate-500">Mail</span>
                <select
                  data-testid="crm-export-step"
                  value={exportStep}
                  onChange={(e) => setExportStep(Number(e.target.value))}
                  className="text-sm bg-transparent outline-none cursor-pointer"
                >
                  <option value={1}>1</option>
                  <option value={2}>2</option>
                  <option value={3}>3</option>
                </select>
                <Button data-testid="crm-export-mails-btn" size="sm" variant="ghost" onClick={exportMails} className="rounded-full h-7 px-2">
                  <Download className="w-4 h-4 mr-1" /> CSV
                </Button>
              </div>
              <div className="basis-full order-last"><ExportUndo API={API} profile={profile} refreshKey={exportNonce} onDone={load} /></div>
              <Button data-testid="crm-helper-btn" size="sm" onClick={() => openHelper(false)} className="rounded-full bg-slate-900 hover:bg-slate-700 text-white">
                <Rocket className="w-4 h-4 mr-1.5" /> Verzendhulp
              </Button>
              {dueRows.length > 0 && (
                <Button data-testid="crm-followup-btn" size="sm" onClick={() => openHelper(true)} className="rounded-full bg-amber-500 hover:bg-amber-600 text-white">
                  <Clock className="w-4 h-4 mr-1.5" /> Opvolgen ({dueRows.length})
                </Button>
              )}
              <input ref={fileRef} type="file" accept=".csv,text/csv" onChange={importReplies} className="hidden" data-testid="crm-import-file" />
              <Button data-testid="crm-import-replies-btn" size="sm" variant="outline" onClick={() => fileRef.current?.click()} disabled={importing} className="rounded-full">
                <Upload className="w-4 h-4 mr-1.5" /> {importing ? "Bezig..." : "Reacties importeren"}
              </Button>
              <Button data-testid="crm-replies-template-btn" size="sm" variant="ghost" onClick={() => window.open(`${API}/crm/replies-template`, "_blank", "noopener")} className="rounded-full text-slate-500" title="Download een voorbeeld-CSV met de juiste kolommen">
                Voorbeeld-CSV
              </Button>
              <Button size="sm" variant="ghost" onClick={load} className="rounded-full"><RefreshCw className="w-4 h-4" /></Button>
              <Button data-testid="crm-reset-pipeline-btn" size="sm" variant="ghost" onClick={resetPipeline} className="rounded-full text-slate-500 hover:text-red-600" title="Zet alle ingeschreven leads terug naar Mail 1 (afgemeld/gereageerd/klant blijft ongemoeid)">
                <RefreshCw className="w-4 h-4 mr-1.5" /> Reset naar Mail 1
              </Button>
              <Button data-testid="crm-repair-failed-btn" size="sm" variant="ghost" onClick={repairFailed} className="rounded-full text-amber-700 hover:bg-amber-50" title="Zet leads terug die door de oude automatische taak onterecht een stap verder stonden (mislukte verzending). Handmatig gemarkeerde verzendingen blijven staan.">
                <Wrench className="w-4 h-4 mr-1.5" /> Herstel onterechte verzendingen
              </Button>
            </div>
          </div>
        )}

        <div className="flex flex-wrap gap-2 my-2" data-testid="crm-counts">
          {Object.keys(STATUS_LABELS).map((k) => c[k] ? (
            <Badge key={k} className={`${STATUS_STYLES[k]} rounded-full px-3 py-1`}>{STATUS_LABELS[k]}: {c[k]}</Badge>
          ) : null)}
          {pipeline.total === 0 && <span className="text-sm text-slate-400">Nog geen leads ingeschreven.</span>}
        </div>

        {pipeline.total > 0 && (
          <div data-testid="crm-unsub-counter" className="flex items-center gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-2.5 my-2">
            <Ban className="w-5 h-5 text-red-500 shrink-0" strokeWidth={1.5} />
            <div>
              <div className="text-xl font-bold text-red-600 leading-none" data-testid="crm-unsub-count">{c.unsubscribed || 0}</div>
              <div className="text-[11px] text-slate-500 mt-0.5">Afgemeld via afmeldlink</div>
            </div>
            <span className="ml-auto text-xs text-slate-400">
              {Math.round(((c.unsubscribed || 0) / pipeline.total) * 100)}% van alle leads
            </span>
          </div>
        )}

        {stats && stats.totals.sent > 0 && (
          <div className="rounded-lg border border-slate-200 p-3" data-testid="crm-dashboard">
            <h3 className="font-display text-base font-bold mb-2 flex items-center gap-2">
              <BarChart3 className="w-4 h-4 text-[#FF4500]" /> Campagne-prestaties
            </h3>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-3">
              {[
                { k: "sent", label: "Verzonden", v: stats.totals.sent, cls: "text-slate-900" },
                { k: "delivered", label: "Afgeleverd", v: stats.totals.delivered, cls: "text-blue-600" },
                { k: "opened", label: `Geopend · ${stats.open_rate}%`, v: stats.totals.opened, cls: "text-emerald-600" },
                { k: "clicked", label: `Geklikt · ${stats.click_rate}%`, v: stats.totals.clicked, cls: "text-[#FF4500]" },
                { k: "bounced", label: `Bounces · ${stats.bounce_rate}%`, v: stats.totals.bounced, cls: "text-red-600" },
              ].map((m) => (
                <div key={m.k} data-testid={`crm-stat-${m.k}`} className="rounded-lg bg-slate-50 px-3 py-2 text-center">
                  <div className={`text-2xl font-bold ${m.cls}`}>{m.v}</div>
                  <div className="text-[11px] text-slate-500">{m.label}</div>
                </div>
              ))}
            </div>
            <table className="w-full text-xs">
              <thead>
                <tr className="text-left text-slate-400 uppercase tracking-wide">
                  <th className="py-1 font-medium">Mail</th><th className="py-1 font-medium">Verzonden</th>
                  <th className="py-1 font-medium">Geopend</th><th className="py-1 font-medium">Geklikt</th>
                  <th className="py-1 font-medium">Bounces</th>
                </tr>
              </thead>
              <tbody>
                {stats.by_step.map((s) => (
                  <tr key={s.step} data-testid={`crm-step-row-${s.step}`} className="border-t border-slate-100 text-slate-700">
                    <td className="py-1 font-medium">Mail {s.step}</td>
                    <td className="py-1">{s.sent}</td><td className="py-1">{s.opened}</td>
                    <td className="py-1">{s.clicked}</td><td className="py-1">{s.bounced}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {stats.weekly && stats.weekly.some((w) => w.sent || w.replies) && (
              <div data-testid="crm-weekly-chart" className="mt-3 pt-3 border-t border-slate-100">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-semibold uppercase tracking-wide text-slate-400">Per week</span>
                  <span className="flex items-center gap-3 text-[11px] text-slate-500">
                    <span className="flex items-center gap-1"><span className="inline-block w-2.5 h-2.5 rounded-sm bg-slate-400" /> Verzonden</span>
                    <span className="flex items-center gap-1"><span className="inline-block w-2.5 h-2.5 rounded-sm bg-emerald-500" /> Reacties</span>
                  </span>
                </div>
                {(() => {
                  const max = Math.max(1, ...stats.weekly.flatMap((w) => [w.sent, w.replies]));
                  return (
                    <div className="flex items-end justify-between gap-1 h-28">
                      {stats.weekly.map((w) => (
                        <div key={w.week} className="flex-1 flex flex-col items-center gap-1" data-testid={`crm-week-${w.week}`}>
                          <div className="flex items-end justify-center gap-0.5 w-full h-24">
                            <div className="w-1/2 max-w-[14px] bg-slate-300 rounded-t transition-all" style={{ height: `${(w.sent / max) * 100}%` }} title={`${w.sent} verzonden`} />
                            <div className="w-1/2 max-w-[14px] bg-emerald-500 rounded-t transition-all" style={{ height: `${(w.replies / max) * 100}%` }} title={`${w.replies} reacties`} />
                          </div>
                          <span className="text-[9px] text-slate-400">{w.week}</span>
                        </div>
                      ))}
                    </div>
                  );
                })()}
              </div>
            )}
              <SubjectStats API={API} profile={profile} refreshKey={exportNonce} />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3 pt-3 border-t border-slate-100">
                <div data-testid="crm-today">
                  <div className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-1">Vandaag verzonden: {stats.today?.total ?? 0}</div>                  <div className="flex flex-col gap-0.5 text-xs text-slate-600">
                    {(stats.today?.by_domain || []).length === 0
                      ? <span className="text-slate-400">Nog niets vandaag.</span>
                      : stats.today.by_domain.map((x) => (
                        <div key={x.domain} className="flex justify-between"><span>{x.domain}</span><span className="font-medium">{x.count}</span></div>
                      ))}
                  </div>
                </div>
                <div data-testid="crm-queue">
                  <div className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-1">In wachtrij: {stats.queue?.total ?? 0}</div>
                  <div className="flex flex-col gap-0.5 text-xs text-slate-600">
                    {(stats.queue?.by_domain || []).length === 0
                      ? <span className="text-slate-400">Wachtrij leeg.</span>
                      : stats.queue.by_domain.map((x) => (
                        <div key={x.domain} className="flex justify-between"><span>{x.domain}</span><span className="font-medium">{x.count}</span></div>
                      ))}
                  </div>
                </div>
              </div>
            {stats.reply_stats && (stats.reply_stats.count > 0) && (
              <div data-testid="crm-reply-stats" className="mt-3 pt-3 border-t border-slate-100 text-sm text-slate-600">
                <span className="font-semibold text-slate-800">{stats.reply_stats.count}</span> reactie(s)
                {stats.reply_stats.avg_days != null
                  ? <> · gemiddelde reactietijd <span className="font-semibold text-emerald-600">{stats.reply_stats.avg_days} dagen</span> <span className="text-slate-400">(o.b.v. {stats.reply_stats.with_timing})</span></>
                  : <span className="text-slate-400"> · reactietijd nog niet te berekenen</span>}
              </div>
            )}
          </div>
        )}

        <HostnetPanel API={API} profile={profile} onChanged={load} />
        <PermissionFollowUp API={API} profile={profile} onChanged={load} />

        {pipeline.rows.length > 0 && (
          <div className="border border-slate-200 rounded-lg overflow-hidden max-h-[300px] overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 sticky top-0">
                <tr className="text-left text-xs uppercase text-slate-500">
                  <th className="px-3 py-2">Vereniging</th><th className="px-3 py-2">Status</th>
                  <th className="px-3 py-2">Stap</th><th className="px-3 py-2">Acties</th>
                </tr>
              </thead>
              <tbody>
                {pipeline.rows.slice(0, 200).map((r) => (
                  <tr key={r.id} data-testid={`crm-row-${r.id}`} className="border-t border-slate-100">
                    <td className="px-3 py-2">
                      <div className="font-medium text-slate-800">{r.name}</div>
                      <div className="text-xs text-slate-400">{r.email}</div>
                      {r.contact && (r.contact.name || r.contact.email || r.contact.phone) && (
                        <div data-testid={`crm-contact-${r.id}`} className="mt-1 inline-flex flex-col rounded-md bg-amber-50 border border-amber-200 px-2 py-1 text-[11px] text-amber-800">
                          <span className="font-semibold uppercase tracking-wide text-[9px] text-amber-500">Contactpersoon</span>
                          <span className="font-medium text-slate-700">
                            {r.contact.name || "—"}{r.contact.title ? ` · ${r.contact.title}` : ""}
                          </span>
                          {(r.contact.email || r.contact.phone) && (
                            <span className="text-slate-500">{[r.contact.email, r.contact.phone].filter(Boolean).join(" · ")}</span>
                          )}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <Badge className={`${STATUS_STYLES[r.status]} rounded-full`}>{STATUS_LABELS[r.status] || r.status}</Badge>
                      {isDue(r) && (
                        <div data-testid={`crm-due-${r.id}`} className="mt-1 inline-flex items-center gap-1 rounded-full bg-amber-100 text-amber-700 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide">
                          <Clock className="w-3 h-3" /> Toe aan mail {r.step + 1}
                        </div>
                      )}
                      {r.status === "active" && r.step < 3 && r.next_send_at && !isDue(r) && (
                        <div className="mt-1 text-[10px] text-slate-400">volgende: {fmtDate(r.next_send_at)}</div>
                      )}
                    </td>
                    <td className="px-3 py-2 text-slate-500">{r.step}/3</td>
                    <td className="px-3 py-2">
                      <div className="flex gap-1">
                        <button data-testid={`crm-copy-mail-${r.id}`} onClick={() => copyMail(r.id)} title="Kopieer volgende mail" className="text-slate-500 hover:text-slate-800"><Copy className="w-4 h-4" /></button>
                        <button data-testid={`crm-contact-edit-${r.id}`} onClick={() => openContact(r)} title="Contactpersoon" className={r.contact && r.contact.name ? "text-amber-600 hover:text-amber-800" : "text-slate-400 hover:text-slate-700"}><Users className="w-4 h-4" /></button>
                        <button data-testid={`crm-mark-replied-${r.id}`} onClick={() => mark(r.id, "replied")} title="Gereageerd" className="text-amber-500 hover:text-amber-700"><MessageSquare className="w-4 h-4" /></button>
                        <button data-testid={`crm-mark-customer-${r.id}`} onClick={() => mark(r.id, "customer")} title="Klant geworden" className="text-emerald-600 hover:text-emerald-800"><CheckCircle2 className="w-4 h-4" /></button>
                        <button data-testid={`crm-mark-unsub-${r.id}`} onClick={() => mark(r.id, "unsubscribed")} title="Afmelden" className="text-red-500 hover:text-red-700"><Ban className="w-4 h-4" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="mt-2">
          <h3 className="font-display text-lg font-bold mb-1">Mail-sjablonen ({profileLabel})</h3>
          <p className="text-xs text-slate-400 mb-3">Variabelen: {"{{aanhef}}"}, {"{{naam}}"}, {"{{plaats}}"}, {"{{afmeldlink}}"} (verplicht in elke mail). Een aanhef als "Beste bestuur(ders) van {"{{naam}}"}" wordt automatisch "Beste Jan Jansen" of "Beste penningmeester" als de contactpersoon bekend is.</p>
          <div className="mb-4 rounded-lg border border-slate-200 p-3 bg-slate-50">
            <div className="text-xs font-semibold text-slate-600 mb-1">Afzenders (1–5, één per regel)</div>
            <p className="text-[11px] text-slate-400 mb-2">Elke lead krijgt vast één van deze adressen (verdeeld over de adressen) in de CSV-kolom <b>Afzender</b>, ook voor Mail 2 en 3. Verstuur vanaf dat adres — daar komen de reacties binnen.</p>
            <div className="flex gap-2 items-start">
              <Textarea data-testid="crm-from-email" rows={3} placeholder={`info@${(settings?.sender_domains || [])[0] || "mjp-offer.nl"}`} value={sendersText} onChange={(e) => setSendersText(e.target.value)} className="text-sm" />
              <Button data-testid="crm-from-save" size="sm" onClick={saveFrom} className="rounded-full bg-slate-900 hover:bg-slate-700 text-white shrink-0">Opslaan</Button>
            </div>
          </div>
          <div className="mb-4 rounded-lg border border-slate-200 p-3 bg-slate-50">
            <div className="text-xs font-semibold text-slate-600 mb-1">Logo / handtekening (afbeelding-URL, optioneel)</div>
            <p className="text-[11px] text-slate-400 mb-2">Komt onder elke mail in de <b>HTML-versie</b> (kolom in de CSV, voor je mail-merge/ESP). De mailto-/tekstversie blijft zonder afbeelding.</p>
            <div className="flex gap-2 items-center">
              <Input data-testid="crm-logo-url" placeholder="https://mjp-online.nl/logo.png" value={settings?.logo_url || ""} onChange={(e) => setSettings((s) => ({ ...s, logo_url: e.target.value }))} className="text-sm" />
              <Button data-testid="crm-logo-save" size="sm" onClick={saveLogo} className="rounded-full bg-slate-900 hover:bg-slate-700 text-white shrink-0">Opslaan</Button>
            </div>
            {settings?.logo_url ? (
              <img src={settings.logo_url} alt="logo preview" className="mt-2 max-h-16 rounded border border-slate-200 bg-white p-1" onError={(e) => { e.currentTarget.style.display = "none"; }} />
            ) : null}
          </div>
          <div className="flex flex-col gap-3">
            {permission && (
              <div data-testid="crm-tpl-permission" className="border border-[#FF4500]/40 bg-orange-50/40 rounded-lg p-3">
                <div className="text-xs font-semibold text-[#FF4500] mb-1">Toestemmingsmail (vóór Mail 1, alleen via Hostnet)</div>
                <p className="text-[11px] text-slate-500 mb-2">Gaat alleen naar leads met bekende functie of naam. Variabelen: {"{{aanhef}}"}, {"{{functie}}"}, {"{{naam}}"}, {"{{plaats}}"}. Antwoordt iemand "ja", klik dan in de tabel op <b>Toestemming ✓</b>.</p>
                <SubjectVariants step="p" subjects={permission.subjects} onChange={(v) => setPermission((p) => ({ ...p, subjects: v }))} />
                <Textarea data-testid="crm-tpl-body-permission" value={permission.body || ""} onChange={(e) => setPermission((p) => ({ ...p, body: e.target.value }))} className="min-h-[140px] text-sm" />
              </div>
            )}
            {steps.map((s, i) => (
              <div key={i} className="border border-slate-200 rounded-lg p-3">
                <div className="text-xs font-semibold text-slate-500 mb-1">Mail {i + 1}</div>
                <SubjectVariants step={i} subjects={s.subjects || [s.subject]} onChange={(v) => setStep(i, "subjects", v)} />
                <Textarea data-testid={`crm-tpl-body-${i}`} value={s.body} onChange={(e) => setStep(i, "body", e.target.value)} className="min-h-[120px] text-sm" />
              </div>
            ))}
          </div>
          <Button data-testid="crm-save-templates" size="sm" onClick={saveTemplates} className="rounded-full mt-3 bg-slate-900 hover:bg-slate-700 text-white">Sjablonen opslaan</Button>
        </div>
      </DialogContent>
    </Dialog>

    <Dialog open={helperOpen} onOpenChange={(v) => (v ? setHelperOpen(true) : closeHelper())}>
      <DialogContent className="bg-white max-w-2xl" data-testid="crm-helper-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2">
            <Rocket className="w-5 h-5 text-[#FF4500]" /> Verzendhulp
          </DialogTitle>
          <DialogDescription>
            Werk je wachtrij af in je eigen tempo. "Open in mijn mail" opent je mailprogramma met alles ingevuld —
            jij klikt alleen op Verzenden, daarna "Verzonden → volgende". Persoonlijke rol-adressen staan vooraan.
          </DialogDescription>
        </DialogHeader>

        <div data-testid="crm-helper-tips" className="flex flex-wrap items-center gap-x-2 gap-y-1 rounded-lg bg-orange-50 border border-orange-200 px-3 py-2 text-xs text-orange-900">
          <span className="font-semibold">Stappen:</span>
          <span>1. "Open in mijn mail"</span>
          <span className="text-orange-300">→</span>
          <span>2. Verzenden in je mailprogramma</span>
          <span className="text-orange-300">→</span>
          <span>3. "Verzonden → volgende"</span>
          <span className="text-orange-300">·</span>
          <span className="text-orange-700">Mail 2 volgt na 4 dagen, mail 3 na 9</span>
        </div>

        <div className="flex flex-wrap items-center gap-2 mt-2" data-testid="crm-helper-stepfilter">
          <span className="text-xs text-slate-500 mr-1">Toon:</span>
          {helperStep === "batch" && (
            <span data-testid="crm-helper-selection-badge" className="text-xs font-semibold text-[#FF4500] bg-orange-50 border border-orange-200 rounded-full px-3 py-1">
              Selectie ({queue.length})
            </span>
          )}
          {(() => {
            const act = (pipeline.rows || []).filter((r) => r.status === "queued" || r.status === "active");
            const cnt = { 1: 0, 2: 0, 3: 0 };
            act.forEach((r) => { const s = (r.step || 0) + 1; if (cnt[s] !== undefined) cnt[s]++; });
            return (
              <>
                <Button
                  size="sm"
                  variant={helperStep === null ? "default" : "outline"}
                  onClick={() => openHelper(false)}
                  data-testid="crm-helper-step-all"
                  className={`rounded-full h-7 px-3 text-xs ${helperStep === null ? "bg-slate-900 text-white" : ""}`}
                >
                  Alle ({act.length})
                </Button>
                {[1, 2, 3].map((s) => (
                  <Button
                    key={s}
                    size="sm"
                    variant={helperStep === s ? "default" : "outline"}
                    onClick={() => pickStep(s)}
                    disabled={cnt[s] === 0}
                    data-testid={`crm-helper-step-${s}`}
                    className={`rounded-full h-7 px-3 text-xs ${helperStep === s ? "bg-[#FF4500] text-white" : ""}`}
                  >
                    Mail {s} ({cnt[s]})
                  </Button>
                ))}
              </>
            );
          })()}
        </div>

        <div className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 bg-slate-50 p-3 mt-2">
          <span className="text-sm text-slate-600" data-testid="crm-helper-progress">
            {queue.length === 0 ? "Wachtrij leeg" :
             qIndex >= queue.length ? "Klaar — hele wachtrij gehad 🎉" :
             `Lead ${qIndex + 1} van ${queue.length}`}
          </span>
          <div className="flex items-center gap-2">
            <Clock className={`w-4 h-4 ${timerOn ? "text-[#FF4500]" : "text-slate-400"}`} />
            <span data-testid="crm-helper-timer" className={`font-mono text-sm ${timerOn ? "text-[#FF4500]" : "text-slate-400"}`}>{fmt(secondsLeft)}</span>
            <Button data-testid="crm-helper-timer-toggle" size="sm" variant="outline" onClick={() => setTimerOn((v) => !v)} className="rounded-full h-7 px-2 text-xs">
              {timerOn ? "Pauze" : "Start 10-min"}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => { setSecondsLeft(600); }} className="rounded-full h-7 px-2 text-xs">Reset</Button>
          </div>
        </div>

        {qIndex < queue.length && current ? (
          <div className="mt-2 border border-slate-200 rounded-lg overflow-hidden" data-testid="crm-helper-card">
            <div className="bg-slate-50 px-4 py-2 border-b border-slate-100 flex flex-wrap items-baseline gap-x-3 gap-y-1">
              <span className="font-semibold text-slate-900">{current.name}</span>
              {current.city && <span className="text-xs text-slate-400">{current.city}</span>}
              <span className="ml-auto text-xs text-slate-500">Mail {current.step}/3</span>
            </div>
            <div className="px-4 py-3 text-sm space-y-1">
              <div className="flex items-center gap-2">
                <span className="text-slate-400">Aan:</span> <span className="text-slate-800">{current.to}</span>
                {addrKind(current.to) && (
                  <span data-testid="crm-helper-addrkind" className={`text-[10px] font-semibold uppercase tracking-wide rounded-full px-2 py-0.5 ${addrKind(current.to) === "persoonlijk" ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                    {addrKind(current.to) === "persoonlijk" ? "Persoonlijk · scoort beter" : "Algemeen"}
                  </span>
                )}
              </div>
              <div><span className="text-slate-400">Van:</span> <span className="text-slate-800">{current.from}</span></div>
              <div><span className="text-slate-400">Onderwerp:</span> <span className="font-medium text-slate-900">{current.subject}</span></div>
              <div className="mt-2 max-h-[180px] overflow-y-auto whitespace-pre-wrap text-slate-700 bg-slate-50 rounded p-2 text-[13px]">{current.body}</div>
            </div>
          </div>
        ) : (
          <div className="py-10 text-center text-slate-400 text-sm" data-testid="crm-helper-empty">
            {queue.length === 0 ? "Schrijf eerst leads in om ze hier te kunnen versturen." : "Alle leads in de wachtrij zijn gehad. Sluit en ververs voor een nieuwe ronde."}
          </div>
        )}

        <DialogFooter className="flex-wrap gap-2">
          <Button data-testid="crm-helper-close" variant="ghost" onClick={closeHelper} className="rounded-full">Sluiten</Button>
          {qIndex < queue.length && current && (
            <>
              <Button data-testid="crm-helper-skip" variant="outline" onClick={() => advance()} className="rounded-full">
                <SkipForward className="w-4 h-4 mr-1.5" /> Overslaan
              </Button>
              <Button data-testid="crm-helper-open-mail" onClick={openInMail} className="rounded-full bg-[#FF4500] hover:bg-[#E63E00] text-white">
                <ExternalLink className="w-4 h-4 mr-1.5" /> Open in mijn mail
              </Button>
              <Button data-testid="crm-helper-copy-html" variant="outline" onClick={copyHtmlMail} className="rounded-full" title="Kopieer de mail als opgemaakte tekst mét logo — plak in Outlook/Gmail">
                <Copy className="w-4 h-4 mr-1.5" /> Kopieer (met logo)
              </Button>
              <Button data-testid="crm-helper-mark-sent" onClick={markSent} disabled={loadingMail} className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white">
                <CheckCircle2 className="w-4 h-4 mr-1.5" /> Verzonden → volgende
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>

    <Dialog open={contactOpen} onOpenChange={setContactOpen}>
      <DialogContent className="bg-white max-w-md" data-testid="crm-contact-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl flex items-center gap-2">
            <Users className="w-5 h-5 text-amber-500" /> Contactpersoon
          </DialogTitle>
          <DialogDescription>
            {contactLead?.name} — leg vast wie er reageerde. De lead komt op "Gereageerd" te staan.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-3">
          <Input data-testid="crm-contact-name" placeholder="Naam" value={contactForm.name} onChange={(e) => setContactForm((f) => ({ ...f, name: e.target.value }))} />
          <Input data-testid="crm-contact-title" placeholder="Functie (bijv. penningmeester)" value={contactForm.title} onChange={(e) => setContactForm((f) => ({ ...f, title: e.target.value }))} />
          <Input data-testid="crm-contact-email" placeholder="E-mail" value={contactForm.email} onChange={(e) => setContactForm((f) => ({ ...f, email: e.target.value }))} />
          <Input data-testid="crm-contact-phone" placeholder="Telefoon" value={contactForm.phone} onChange={(e) => setContactForm((f) => ({ ...f, phone: e.target.value }))} />
        </div>
        <DialogFooter className="flex-wrap gap-2">
          {contactLead?.contact && contactLead.contact.name && (
            <Button data-testid="crm-contact-clear" variant="ghost" onClick={clearContact} className="rounded-full text-red-500 hover:text-red-700 mr-auto">
              <X className="w-4 h-4 mr-1.5" /> Verwijderen
            </Button>
          )}
          <Button variant="ghost" onClick={() => setContactOpen(false)} className="rounded-full">Annuleren</Button>
          <Button data-testid="crm-contact-save" onClick={saveContact} className="rounded-full bg-amber-500 hover:bg-amber-600 text-white">Opslaan</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    </>
  );
}
